import { useEffect, useState } from "react";
import api from "../lib/api";
import { Plus, Edit2, Trash2, User, Image as ImageIcon, X, Save, MapPin } from "lucide-react";
import { toast } from "sonner";
import AdminLayout from "../components/AdminLayout";
import { formatApiError } from "../lib/errors";
import { compressImage, uploadImage } from "../lib/image";

function fileToBase64(file) {
    return uploadImage(file, api).catch(() => compressImage(file)).catch(() => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result);
            reader.onerror = reject;
            reader.readAsDataURL(file);
        });
    });
}

const empty = {
    name: "", role: "ambassador", chapter: "Delhi NCR", title: "", bio: "", photo_base64: "",
    instagram: "", order: 0,
    ride: "", favorite_route: "", dream_ride: "", ride_fuel: "", ride_style: "", signature: "",
    period_from: "", period_to: "",
};

function MemberForm({ initial, onCancel, onSaved }) {
    const [form, setForm] = useState(initial || empty);
    const [saving, setSaving] = useState(false);
    const [chapters, setChapters] = useState([]);
    const [addingChapter, setAddingChapter] = useState(false);
    const [newChapter, setNewChapter] = useState("");
    const [savingChapter, setSavingChapter] = useState(false);

    useEffect(() => {
        api.get("/chapters").then((r) => setChapters(r.data?.values || [])).catch(() => {});
    }, []);

    const addChapter = async () => {
        const name = newChapter.trim();
        if (!name) return;
        if (chapters.some((c) => c.toLowerCase() === name.toLowerCase())) {
            setForm((f) => ({ ...f, chapter: chapters.find((c) => c.toLowerCase() === name.toLowerCase()) }));
            setAddingChapter(false);
            setNewChapter("");
            return;
        }
        setSavingChapter(true);
        try {
            const { data } = await api.put("/chapters", { values: [...chapters, name] });
            setChapters(data.values || []);
            setForm((f) => ({ ...f, chapter: name }));
            setAddingChapter(false);
            setNewChapter("");
            toast.success(`Chapter "${name}" added`);
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail, err));
        } finally {
            setSavingChapter(false);
        }
    };

    const onFile = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (file.size > 5 * 1024 * 1024) {
            toast.error("Original file is too large. Pick something under 5MB.");
            return;
        }
        try {
            const b64 = await fileToBase64(file);
            setForm((f) => ({ ...f, photo_base64: b64 }));
        } catch {
            toast.error("Couldn't process this image. Try a different file.");
        }
    };

    const submit = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            if (form.id) {
                const { id, created_at, ...payload } = form;
                await api.put(`/members/${id}`, payload);
                toast.success("Member updated");
            } else {
                await api.post("/members", form);
                toast.success("Member added");
            }
            onSaved();
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail, err));
        } finally {
            setSaving(false);
        }
    };

    return (
        <form onSubmit={submit} className="bg-meraki-surface border border-meraki-gold/20 p-6 md:p-8 space-y-5" data-testid="member-form">
            <h3 className="font-heading uppercase tracking-[0.25em] text-meraki-gold text-sm mb-2">
                {form.id ? "Edit member" : "Add new member"}
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <Field label="Name">
                    <input data-testid="form-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} />
                </Field>
                <Field label="Role">
                    <select data-testid="form-role" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className={inputCls}>
                        <option value="cofounder">Cofounder</option>
                        <option value="ambassador">Ambassador</option>
                    </select>
                </Field>
                <Field label="Meraki Chapter">
                    {addingChapter ? (
                        <div className="flex gap-2">
                            <input
                                data-testid="new-chapter-input"
                                autoFocus
                                value={newChapter}
                                onChange={(e) => setNewChapter(e.target.value)}
                                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addChapter(); } }}
                                placeholder="e.g. Mumbai"
                                className={`${inputCls} flex-1`}
                            />
                            <button
                                type="button"
                                data-testid="save-chapter-btn"
                                onClick={addChapter}
                                disabled={savingChapter}
                                className="px-4 py-3 bg-meraki-gold text-black text-xs uppercase tracking-[0.2em] font-bold hover:bg-meraki-gold-hover transition-colors disabled:opacity-50 whitespace-nowrap"
                            >
                                {savingChapter ? "…" : "Add"}
                            </button>
                            <button
                                type="button"
                                onClick={() => { setAddingChapter(false); setNewChapter(""); }}
                                className="px-4 py-3 border border-meraki-gold/30 text-meraki-muted text-xs uppercase tracking-[0.2em] hover:border-meraki-gold whitespace-nowrap"
                            >
                                Cancel
                            </button>
                        </div>
                    ) : (
                        <select
                            data-testid="form-chapter"
                            value={form.chapter || ""}
                            onChange={(e) => {
                                if (e.target.value === "__add__") { setAddingChapter(true); return; }
                                setForm({ ...form, chapter: e.target.value });
                            }}
                            className={inputCls}
                        >
                            <option value="">— Select chapter —</option>
                            {chapters.map((c) => (
                                <option key={c} value={c}>{c}</option>
                            ))}
                            {form.chapter && !chapters.includes(form.chapter) && (
                                <option value={form.chapter}>{form.chapter}</option>
                            )}
                            <option value="__add__">+ Add new chapter…</option>
                        </select>
                    )}
                </Field>
                <Field label="Title / Subtitle">
                    <input data-testid="form-title" value={form.title || ""} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="e.g. Cofounder · Ride Lead" className={inputCls} />
                </Field>
                <Field label="Instagram">
                    <input data-testid="form-instagram" value={form.instagram || ""} onChange={(e) => setForm({ ...form, instagram: e.target.value })} placeholder="@username or full URL" className={inputCls} />
                </Field>
                <Field label="Display Order">
                    <input data-testid="form-order" type="number" value={form.order ?? 0} onChange={(e) => setForm({ ...form, order: parseInt(e.target.value || "0", 10) })} className={inputCls} />
                </Field>
                <Field label="Tenure — From (month/year)">
                    <input data-testid="form-period-from" type="month" value={form.period_from || ""} onChange={(e) => setForm({ ...form, period_from: e.target.value })} className={inputCls} />
                </Field>
                <Field label="Tenure — To (leave empty for 'Present')">
                    <div className="flex gap-2 items-center">
                        <input data-testid="form-period-to" type="month" value={form.period_to || ""} onChange={(e) => setForm({ ...form, period_to: e.target.value })} className={inputCls} />
                        {form.period_to && (
                            <button type="button" onClick={() => setForm({ ...form, period_to: "" })} className="px-3 py-3 border border-meraki-gold/30 text-meraki-muted hover:text-meraki-gold text-[10px] uppercase tracking-[0.2em] whitespace-nowrap">
                                Set Present
                            </button>
                        )}
                    </div>
                </Field>
            </div>

            <Field label="Bio">
                <textarea data-testid="form-bio" rows={3} value={form.bio || ""} onChange={(e) => setForm({ ...form, bio: e.target.value })} className={inputCls} placeholder="3–5 lines about this rider…" />
            </Field>

            <div className="border-t border-meraki-gold/15 pt-5">
                <div className="text-[10px] uppercase tracking-[0.3em] text-meraki-gold mb-4">Rider Profile (Quick Stats)</div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <Field label="🏍️ Ride">
                        <input data-testid="form-ride" value={form.ride || ""} onChange={(e) => setForm({ ...form, ride: e.target.value })} placeholder="e.g. Africa Twin / Interceptor 650" className={inputCls} />
                    </Field>
                    <Field label="📍 Favorite Route">
                        <input data-testid="form-fav-route" value={form.favorite_route || ""} onChange={(e) => setForm({ ...form, favorite_route: e.target.value })} placeholder="e.g. Manali · Spiti · Chakrata" className={inputCls} />
                    </Field>
                    <Field label="⛰️ Dream Ride">
                        <input data-testid="form-dream-ride" value={form.dream_ride || ""} onChange={(e) => setForm({ ...form, dream_ride: e.target.value })} placeholder="e.g. Ladakh / Adi Kailash" className={inputCls} />
                    </Field>
                    <Field label="☕ Ride Fuel">
                        <input data-testid="form-ride-fuel" value={form.ride_fuel || ""} onChange={(e) => setForm({ ...form, ride_fuel: e.target.value })} placeholder="e.g. Chai / Coffee" className={inputCls} />
                    </Field>
                    <Field label="🌄 Ride Style">
                        <input data-testid="form-ride-style" value={form.ride_style || ""} onChange={(e) => setForm({ ...form, ride_style: e.target.value })} placeholder="e.g. Chill / Aggressive / Explorer" className={inputCls} />
                    </Field>
                </div>
                <div className="mt-5">
                    <Field label="❤️ Signature Line (personal quote or philosophy)">
                        <textarea data-testid="form-signature" rows={2} value={form.signature || ""} onChange={(e) => setForm({ ...form, signature: e.target.value })} placeholder='e.g. "Two wheels move the soul."' className={inputCls} />
                    </Field>
                </div>
            </div>

            <Field label="Photo (max 2MB)">
                <div className="flex items-center gap-4">
                    <div className="w-20 h-20 border border-meraki-gold/20 bg-black flex items-center justify-center overflow-hidden">
                        {form.photo_base64 ? <img src={form.photo_base64} alt="" className="w-full h-full object-cover" /> : <ImageIcon className="w-6 h-6 text-meraki-muted" />}
                    </div>
                    <input data-testid="form-photo" type="file" accept="image/*" onChange={onFile} className="text-xs text-meraki-muted file:mr-4 file:py-2 file:px-4 file:border file:border-meraki-gold/30 file:bg-transparent file:text-meraki-gold file:uppercase file:tracking-wider file:text-[10px] hover:file:bg-meraki-gold/10" />
                </div>
            </Field>

            <div className="flex items-center gap-3 pt-4">
                <button data-testid="form-save-btn" type="submit" disabled={saving} className="px-6 py-3 bg-meraki-gold text-black text-xs uppercase tracking-[0.25em] font-bold hover:bg-meraki-gold-hover transition-colors disabled:opacity-50">
                    {saving ? "Saving..." : form.id ? "Update" : "Add Member"}
                </button>
                <button type="button" onClick={onCancel} className="px-6 py-3 border border-meraki-gold/30 text-meraki-text text-xs uppercase tracking-[0.25em] hover:border-meraki-gold transition-colors">Cancel</button>
            </div>
        </form>
    );
}

const inputCls = "w-full bg-black border border-meraki-gold/20 px-4 py-3 text-meraki-text focus:outline-none focus:border-meraki-gold";

function ChaptersManager({ onClose, onChanged }) {
    const [rows, setRows] = useState([]);
    const [newName, setNewName] = useState("");
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        api.get("/chapters")
            .then((r) => setRows((r.data?.values || []).map((v) => ({ original: v, value: v }))))
            .catch(() => {});
    }, []);

    const addRow = () => {
        const name = newName.trim();
        if (!name) return;
        if (rows.some((r) => r.value.toLowerCase() === name.toLowerCase())) {
            toast.error("That chapter already exists");
            return;
        }
        setRows([...rows, { original: null, value: name }]);
        setNewName("");
    };

    const save = async () => {
        const values = rows.map((r) => r.value.trim()).filter(Boolean);
        if (values.length === 0) {
            toast.error("Keep at least one chapter");
            return;
        }
        const renames = {};
        rows.forEach((r) => {
            const v = r.value.trim();
            if (r.original && v && r.original !== v) renames[r.original] = v;
        });
        setSaving(true);
        try {
            await api.put("/chapters", { values, renames: Object.keys(renames).length ? renames : undefined });
            toast.success("Chapters updated");
            onChanged?.();
            onClose();
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail, err));
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="bg-meraki-surface border border-meraki-gold/20 p-6 md:p-8 space-y-5" data-testid="chapters-manager">
            <div className="flex items-center justify-between">
                <h3 className="font-heading uppercase tracking-[0.25em] text-meraki-gold text-sm">Manage Chapters</h3>
                <button type="button" onClick={onClose} className="text-meraki-muted hover:text-meraki-gold" aria-label="Close"><X className="w-4 h-4" /></button>
            </div>
            <p className="text-xs text-meraki-muted">Rename or remove chapters. Renaming a chapter automatically updates every rider assigned to it.</p>

            <div className="space-y-2">
                {rows.map((r, i) => (
                    <div key={`${r.original ?? "new"}-${i}`} className="flex items-center gap-2">
                        <MapPin className="w-4 h-4 text-meraki-gold shrink-0" />
                        <input
                            data-testid={`chapter-row-${i}`}
                            value={r.value}
                            onChange={(e) => setRows(rows.map((row, j) => (j === i ? { ...row, value: e.target.value } : row)))}
                            className={`${inputCls} flex-1`}
                        />
                        <button
                            type="button"
                            data-testid={`chapter-remove-${i}`}
                            onClick={() => setRows(rows.filter((_, j) => j !== i))}
                            className="p-3 border border-red-900/40 hover:border-red-700 text-red-400 transition-colors"
                            aria-label="Remove chapter"
                        >
                            <Trash2 className="w-3.5 h-3.5" />
                        </button>
                    </div>
                ))}
            </div>

            <div className="flex gap-2">
                <input
                    data-testid="chapter-new-name"
                    value={newName}
                    onChange={(e) => setNewName(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addRow(); } }}
                    placeholder="Add a chapter…"
                    className={`${inputCls} flex-1`}
                />
                <button type="button" data-testid="chapter-add-row" onClick={addRow} className="px-4 py-3 border border-meraki-gold/30 text-meraki-gold text-xs uppercase tracking-[0.2em] hover:border-meraki-gold whitespace-nowrap inline-flex items-center gap-2">
                    <Plus className="w-3.5 h-3.5" /> Add
                </button>
            </div>

            <div className="flex items-center gap-3 pt-4 border-t border-meraki-gold/15">
                <button data-testid="chapters-save-btn" type="button" onClick={save} disabled={saving} className="px-6 py-3 bg-meraki-gold text-black text-xs uppercase tracking-[0.25em] font-bold hover:bg-meraki-gold-hover transition-colors disabled:opacity-50 inline-flex items-center gap-2">
                    <Save className="w-4 h-4" /> {saving ? "Saving…" : "Save Chapters"}
                </button>
                <button type="button" onClick={onClose} className="px-6 py-3 border border-meraki-gold/30 text-meraki-text text-xs uppercase tracking-[0.25em] hover:border-meraki-gold transition-colors">Cancel</button>
            </div>
        </div>
    );
}

function Field({ label, children }) {
    return (
        <div>
            <label className="block text-[10px] uppercase tracking-[0.25em] text-meraki-muted mb-2">{label}</label>
            {children}
        </div>
    );
}

export default function AdminDashboard() {
    const [members, setMembers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(null);
    const [showChapters, setShowChapters] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/members");
            setMembers(data || []);
        } catch {
            toast.error("Failed to load members");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    const onDelete = async (id) => {
        if (!window.confirm("Delete this member?")) return;
        try {
            await api.delete(`/members/${id}`);
            toast.success("Member removed");
            load();
        } catch {
            toast.error("Delete failed");
        }
    };

    const cofounders = members.filter((m) => m.role === "cofounder");
    const ambassadors = members.filter((m) => m.role === "ambassador");

    return (
        <AdminLayout>
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
                <div>
                    <div className="text-[10px] uppercase tracking-[0.4em] text-meraki-gold mb-3">Manage Leaders</div>
                    <h1 className="font-display text-4xl md:text-5xl uppercase text-meraki-text leading-tight">Co-Founders &<br />Ambassadors</h1>
                </div>
                <div className="flex flex-wrap items-center gap-3">
                    <button data-testid="manage-chapters-btn" onClick={() => setShowChapters((s) => !s)} className="flex items-center gap-2 px-6 py-3 border border-meraki-gold/40 text-meraki-gold text-xs uppercase tracking-[0.25em] font-bold hover:border-meraki-gold hover:bg-meraki-gold/10 transition-colors">
                        <MapPin className="w-4 h-4" /> Manage Chapters
                    </button>
                    <button data-testid="add-member-btn" onClick={() => setEditing("new")} className="flex items-center gap-2 px-6 py-3 bg-meraki-gold text-black text-xs uppercase tracking-[0.25em] font-bold hover:bg-meraki-gold-hover transition-colors">
                        <Plus className="w-4 h-4" /> Add Member
                    </button>
                </div>
            </div>

            {showChapters && (
                <div className="mb-12">
                    <ChaptersManager onClose={() => setShowChapters(false)} onChanged={load} />
                </div>
            )}

            {editing && (
                <div className="mb-12">
                    <MemberForm initial={editing === "new" ? null : editing} onCancel={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />
                </div>
            )}

            <div className="space-y-12">
                <Section title="Co-Founders" count={cofounders.length} members={cofounders} loading={loading} onEdit={setEditing} onDelete={onDelete} />
                <Section title="Ambassadors" count={ambassadors.length} members={ambassadors} loading={loading} onEdit={setEditing} onDelete={onDelete} />
            </div>
        </AdminLayout>
    );
}

function Section({ title, count, members, loading, onEdit, onDelete }) {
    return (
        <div>
            <div className="flex items-baseline justify-between border-b border-meraki-gold/20 pb-3 mb-6">
                <h2 className="font-heading uppercase tracking-[0.25em] text-meraki-gold text-sm">{title}</h2>
                <span className="text-xs text-meraki-muted">{count} total</span>
            </div>
            {loading ? (
                <div className="text-meraki-muted text-sm">Loading...</div>
            ) : members.length === 0 ? (
                <div className="border border-dashed border-meraki-gold/20 p-8 text-center text-sm text-meraki-muted">No {title.toLowerCase()} yet</div>
            ) : (
                <div className="border border-meraki-gold/20 overflow-x-auto">
                    <table className="w-full" data-testid={`table-${title.toLowerCase().replace(/\s/g, "-")}`}>
                        <thead className="bg-meraki-surface">
                            <tr className="text-[10px] uppercase tracking-[0.25em] text-meraki-muted">
                                <th className="text-left p-4 w-16">Photo</th>
                                <th className="text-left p-4">Name</th>
                                <th className="text-left p-4 hidden md:table-cell">Chapter</th>
                                <th className="text-left p-4 hidden md:table-cell">Title</th>
                                <th className="text-left p-4 hidden md:table-cell">Instagram</th>
                                <th className="text-left p-4 w-20">Order</th>
                                <th className="text-right p-4 w-32">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {members.map((m) => (
                                <tr key={m.id} className="border-t border-meraki-gold/10 hover:bg-meraki-surface/50">
                                    <td className="p-4">
                                        <div className="w-10 h-10 bg-black border border-meraki-gold/20 overflow-hidden flex items-center justify-center">
                                            {m.photo_base64 ? <img src={m.photo_base64} alt="" className="w-full h-full object-cover" /> : <User className="w-5 h-5 text-meraki-muted" />}
                                        </div>
                                    </td>
                                    <td className="p-4 text-meraki-text">{m.name}</td>
                                    <td className="p-4 text-meraki-muted hidden md:table-cell text-xs">{m.chapter || "—"}</td>
                                    <td className="p-4 text-meraki-muted hidden md:table-cell">{m.title || "—"}</td>
                                    <td className="p-4 text-meraki-muted hidden md:table-cell text-xs">{m.instagram || "—"}</td>
                                    <td className="p-4 text-meraki-muted">{m.order ?? 0}</td>
                                    <td className="p-4 text-right">
                                        <div className="inline-flex gap-2">
                                            <button data-testid={`edit-${m.id}`} onClick={() => onEdit(m)} className="p-2 border border-meraki-gold/30 hover:border-meraki-gold text-meraki-gold transition-colors">
                                                <Edit2 className="w-3 h-3" />
                                            </button>
                                            <button data-testid={`delete-${m.id}`} onClick={() => onDelete(m.id)} className="p-2 border border-red-900/40 hover:border-red-700 text-red-400 transition-colors">
                                                <Trash2 className="w-3 h-3" />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}
